import { useSettings } from "@sinch/core/providers/AppSettings";
import { forceBoundary } from "@sinch/utils/math";
import { removeExceptFirst } from "@sinch/utils/string";
import { isNotNil } from "ramda-adjunct";
import { ChangeEvent, useCallback, useState } from "react";

/**
 * Interface representing the properties for the useNumberInputValue hook.
 */
interface UseNumberInputValueProps {
  /**
   * The current value of the input.
   */
  value: number | string;

  /**
   * Whether to allow empty input.
   */
  allowEmpty?: boolean;

  /**
   * Callback function to handle changes to the input value.
   */
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;

  /**
   * The minimum value allowed for the input. No limit if undefined
   */
  min?: number;

  /**
   * The maximum value allowed for the input. No limit if undefined
   */
  max?: number;
}

interface UseNumberInputReturn {
  /**
   * Function to handle changes to the input value. Force a value to be a number.
   */
  toNumber: (value: string) => string;

  /**
   * Function to handle blur events on the input field. Trigger convertion to number if field blur.
   */
  toClosestLimit: (value: string) => string;

  /**
   * The current value of the input.
   */
  localizedValue: string;

  value: number | string;
}
/**
 * Custom hook to manage the value of a number input field.
 *
 * // TODO: Change with use of Decimal.js
 */
export const useNumberInputValue = ({
  value,
  allowEmpty,
  onChange,
  min,
  max,
}: UseNumberInputValueProps): UseNumberInputReturn => {
  const { locale } = useSettings();
  const decimalSeparator = getDecimalSeparator(locale) ?? ".";
  const [empty, setEmpty] = useState(false);
  const [temporaryInvalidVal, setTemporaryInvalidVal] = useState<string | null>();

  const toNumber = useCallback(
    (val: string) => {
      if (val === "") {
        setEmpty(true);
        setTemporaryInvalidVal(null);
        if (allowEmpty) {
          return val;
        }
        // special case for number input, if just dot, add zero before
      } else if (val === "-") {
        setEmpty(false);
        setTemporaryInvalidVal("-");
        return "0";
      } else if (val === "." || val === decimalSeparator || val === "-") {
        setEmpty(false);
        setTemporaryInvalidVal("0.");
        return "0";
      } else {
        setEmpty(false);
        const toDotDivider = val.replaceAll(decimalSeparator, ".");
        const numString = removeExceptFirst(
          toDotDivider.replace(new RegExp(`[^\\d.${decimalSeparator}-]`, "g"), ""),
          "."
        ).replace(/(?!^)-/g, "");

        if (numString.endsWith(".")) {
          setTemporaryInvalidVal(numString);
          return numString + "0";
        }
        setTemporaryInvalidVal(null);
        return numString;
      }
      setTemporaryInvalidVal(null);
      return val;
    },
    [allowEmpty, onChange]
  );

  const toClosestLimit = useCallback(
    (val: string) => {
      setTemporaryInvalidVal(null);
      if (allowEmpty && val === "") {
        return val;
      }
      if (val.trim() === "") {
        setEmpty(false);
        return String(min || 0);
      } else if (val.trim() === "-") {
        setEmpty(false);
        return String(0);
      } else {
        setEmpty(false);
        const toDotDivider = val.replaceAll(decimalSeparator, ".");
        return String(forceBoundary(min, max)(+toDotDivider));
      }
    },
    [allowEmpty, min, max, onChange]
  );

  const localizedValue = isNotNil(value) ? String(value).replaceAll(".", decimalSeparator) : value;
  return {
    toNumber,
    toClosestLimit,
    localizedValue: temporaryInvalidVal ?? (empty ? "" : localizedValue),
    value: temporaryInvalidVal ?? (empty ? "" : value),
  };
};

const getDecimalSeparator = (locale: string) =>
  Intl.NumberFormat(locale)
    .formatToParts(1.1)
    .find((part) => part.type === "decimal")?.value;
